<template>
  <div class="apidocs-page">
    <!-- 页面说明 -->
    <el-alert
      title="接口文档 - 方案B轻量化系统完整API清单"
      type="info"
      :closable="false"
      show-icon
      style="margin-bottom: 16px"
    >
      <div slot="default">
        所有接口均已实现可用。按功能模块分组展示，支持搜索过滤。
      </div>
    </el-alert>

    <!-- 快捷入口卡片 -->
    <el-row :gutter="16" style="margin-bottom: 16px">
      <el-col :span="6">
        <el-card shadow="hover" class="entry-card" @click.native="scrollToGroup('firstbook')">
          <div class="entry-icon" style="background:#ecf5ff">
            <i class="el-icon-document" style="color:#409EFF"></i>
          </div>
          <div class="entry-content">
            <div class="entry-title">第一本账</div>
            <div class="entry-desc">自选池管理接口</div>
          </div>
        </el-card>
      </el-col>
      <el-col :span="6">
        <el-card shadow="hover" class="entry-card" @click.native="scrollToGroup('stock_kline')">
          <div class="entry-icon" style="background:#fdf6ec">
            <i class="el-icon-data-line" style="color:#E6A23C"></i>
          </div>
          <div class="entry-content">
            <div class="entry-title">行情与K线</div>
            <div class="entry-desc">股票数据接口</div>
          </div>
        </el-card>
      </el-col>
      <el-col :span="6">
        <el-card shadow="hover" class="entry-card" @click.native="scrollToGroup('chat')">
          <div class="entry-icon" style="background:#f0f9eb">
            <i class="el-icon-chat-line-round" style="color:#67C23A"></i>
          </div>
          <div class="entry-content">
            <div class="entry-title">聊天分析</div>
            <div class="entry-desc">分析请求与结论</div>
          </div>
        </el-card>
      </el-col>
      <el-col :span="6">
        <el-card shadow="hover" class="entry-card" @click.native="scrollToGroup('tasks')">
          <div class="entry-icon" style="background:#fef0f0">
            <i class="el-icon-timer" style="color:#F56C6C"></i>
          </div>
          <div class="entry-content">
            <div class="entry-title">定时任务</div>
            <div class="entry-desc">任务调度接口</div>
          </div>
        </el-card>
      </el-col>
    </el-row>

    <!-- 搜索 -->
    <el-card shadow="never" style="margin-bottom: 16px">
      <div slot="header" style="display:flex;align-items:center;justify-content:space-between">
        <span><i class="el-icon-search"></i> 搜索接口</span>
        <span style="font-size:13px;color:#606266">总计 <b>{{ totalCount }}</b> 个接口</span>
      </div>
      <el-input v-model="search" placeholder="搜索接口路径或描述..." size="small" style="width:320px" clearable prefix-icon="el-icon-search" />
    </el-card>

    <!-- 接口分组列表 -->
    <el-collapse v-model="activeGroups">
      <el-collapse-item v-for="group in filteredGroups" :key="group.key" :name="group.key">
        <template slot="title">
          <div class="group-title-row">
            <span class="group-emoji">{{ group.emoji }}</span>
            <span class="group-name">{{ group.title }}</span>
            <el-tag size="mini" type="info" effect="plain" style="margin-left:8px">{{ group.apis.length }} 个接口</el-tag>
          </div>
        </template>

        <el-table :data="group.apis.filter(a => filterMatch(a))" border stripe size="small" style="width:100%">
          <el-table-column type="expand">
            <template slot-scope="{row}">
              <div class="endpoint-detail">
                <div class="detail-section" v-if="row.params && row.params.length">
                  <h4>请求参数</h4>
                  <el-table :data="row.params" border size="mini" style="width:100%">
                    <el-table-column label="参数" prop="name" width="160"><template slot-scope="{row}"><code>{{ row.name }}</code></template></el-table-column>
                    <el-table-column label="位置" prop="in" width="80" align="center">
                      <template slot-scope="{row}">
                        <el-tag :type="row.in === 'body' ? 'warning' : 'info'" size="mini">{{ row.in }}</el-tag>
                      </template>
                    </el-table-column>
                    <el-table-column label="必填" width="60" align="center"><template slot-scope="{row}">{{ row.required ? '是' : '' }}</template></el-table-column>
                    <el-table-column label="类型" prop="type" width="90" />
                    <el-table-column label="说明" prop="desc" min-width="180" />
                  </el-table>
                </div>
                <div class="detail-section" v-if="row.response && row.response.length">
                  <h4>返回字段</h4>
                  <el-table :data="row.response" border size="mini" style="width:100%">
                    <el-table-column label="字段" prop="name" width="180"><template slot-scope="{row}"><code>{{ row.name }}</code></template></el-table-column>
                    <el-table-column label="类型" prop="type" width="90" />
                    <el-table-column label="说明" prop="desc" min-width="220" />
                  </el-table>
                </div>
                <div v-if="!row.params.length && !row.response.length" class="detail-empty">暂无详细参数文档</div>
              </div>
            </template>
          </el-table-column>
          <el-table-column label="方法" width="65" align="center">
            <template slot-scope="{row}">
              <el-tag :type="methodType(row.method)" size="mini" effect="dark" style="font-family:monospace">{{ row.method }}</el-tag>
            </template>
          </el-table-column>
          <el-table-column label="接口路径" width="300">
            <template slot-scope="{row}">
              <code class="api-path">{{ row.fullPath }}</code>
            </template>
          </el-table-column>
          <el-table-column label="描述" min-width="300" show-overflow-tooltip>
            <template slot-scope="{row}">{{ row.desc }}</template>
          </el-table-column>
        </el-table>
      </el-collapse-item>
    </el-collapse>
  </div>
</template>

<script>
const METHODS = { GET: '', POST: 'success', PUT: 'warning', DELETE: 'danger', PATCH: '' }

const Q = (name, type, required, desc) => ({ name, type, required, in:'query', desc })
const P = (name, type, required, desc) => ({ name, type, required, in:'path', desc })
const B = (name, type, required, desc) => ({ name, type, required, in:'body', desc })
const R = (name, type, desc) => ({ name, type, desc })

function buildGroups() {
  return [
    {
      key: 'root', emoji: '', title: '系统根接口',
      apis: [
        { method:'GET', path:'/', fullPath:'GET /', desc:'系统信息', params: [], response: [R('name','string','系统名称'),R('version','string','版本号'),R('status','string','运行状态')] },
        { method:'GET', path:'/health', fullPath:'GET /health', desc:'健康检查', params: [], response: [R('status','string','healthy')] },
      ]
    },
    {
      key: 'firstbook', emoji: '', title: '第一本账',
      apis: [
        { method:'GET', path:'/first-book/current', fullPath:'GET /api/first-book/current', desc:'获取当前第一本账数据', params: [], response: [R('data','array','股票列表'),R('formal_count','int','正式区数量'),R('temp_count','int','临时区数量')] },
        { method:'GET', path:'/first-book/suggestions', fullPath:'GET /api/first-book/suggestions', desc:'股票搜索建议', params: [Q('q','string',true,'搜索关键词'),Q('limit','int',false,'返回数量')], response: [R('data','array','[{ts_code,name,code6,industry,area}]')] },
        { method:'GET', path:'/first-book/popular-stocks', fullPath:'GET /api/first-book/popular-stocks', desc:'热门股票列表', params: [], response: [R('data','array','热门股票')] },
        { method:'GET', path:'/first-book/symbol/{ts_code}', fullPath:'GET /api/first-book/symbol/{ts_code}', desc:'单票详情(含实时行情)', params: [P('ts_code','string',true,'股票代码')], response: [R('symbol','object','股票完整信息'),R('quote','object','实时行情')] },
        { method:'GET', path:'/first-book/stats', fullPath:'GET /api/first-book/stats', desc:'第一本账统计', params: [], response: [R('stats','object','统计信息')] },
        { method:'POST', path:'/first-book/diff', fullPath:'POST /api/first-book/diff', desc:'第一本账差异对比', params: [B('data','object',true,'对比请求数据')], response: [R('diff','object','差异结果')] },
        { method:'POST', path:'/first-book/confirm', fullPath:'POST /api/first-book/confirm', desc:'确认调整', params: [B('adjustments','array',true,'调整列表')], response: [R('message','string','')] },
      ]
    },
    {
      key: 'stock_kline', emoji: '', title: '股票行情与K线',
      apis: [
        { method:'GET', path:'/stock/suggestions', fullPath:'GET /api/stock/suggestions', desc:'全量A股搜索建议', params: [Q('q','string',false,'搜索关键词')], response: [R('data','array','[{ts_code,name,code6,industry}]')] },
        { method:'GET', path:'/stock/irm-qa', fullPath:'GET /api/stock/irm-qa', desc:'董秘互动易问答', params: [Q('ts_code','string',false,'股票代码'),Q('days','int',false,'默认30天')], response: [R('stocks','array','[{ts_code,name,has_data,qa_list}]')] },
        { method:'GET', path:'/stock/kline/{ts_code}', fullPath:'GET /api/stock/kline/{ts_code}', desc:'K线数据(日/周/月)', params: [P('ts_code','string',true,'股票代码'),Q('period','string',false,'时间范围')], response: [R('','array','[{trade_date,open,high,low,close,vol}]')] },
        { method:'POST', path:'/stock/analysis-payload', fullPath:'POST /api/stock/analysis-payload', desc:'单票分析数据包', params: [B('ts_code','string',true,'股票代码')], response: [R('machine_payload','object','含K线/指标等完整数据')] },
        { method:'GET', path:'/kline/realtime', fullPath:'GET /api/kline/realtime', desc:'实时行情快照', params: [Q('ts_code','string',true,'股票代码')], response: [R('','object','{price,change,change_pct,...}')] },
      ]
    },
    {
      key: 'market', emoji: '', title: '市场数据',
      apis: [
        { method:'GET', path:'/market/index-quotes', fullPath:'GET /api/market/index-quotes', desc:'核心指数实时行情', params: [], response: [R('','array','8大指数行情')] },
        { method:'GET', path:'/market/lhb', fullPath:'GET /api/market/lhb', desc:'龙虎榜', params: [Q('trade_date','string',false,'交易日期')], response: [R('','array','龙虎榜数据')] },
        { method:'GET', path:'/market/north-bound', fullPath:'GET /api/market/north-bound', desc:'北向资金快照', params: [], response: [R('','object','北向资金数据')] },
        { method:'GET', path:'/market/stock-fund-flow', fullPath:'GET /api/market/stock-fund-flow', desc:'单票资金流向', params: [Q('ts_code','string',true,'股票代码')], response: [R('','object','资金流向')] },
      ]
    },
    {
      key: 'chat', emoji: '', title: '聊天触发分析',
      apis: [
        { method:'POST', path:'/analysis/request', fullPath:'POST /api/analysis/request', desc:'创建分析请求', params: [Q('intent_type','string',true,'分析意图'),Q('target_type','string',true,'目标类型'),Q('target_value','string',true,'目标值')], response: [R('request_id','string',''),R('status','string','')] },
        { method:'POST', path:'/analysis/payload', fullPath:'POST /api/analysis/payload', desc:'生成分析载荷', params: [B('request_id','string',true,'请求ID'),B('payload_type','string',true,'载荷类型')], response: [R('payload','object','machine_payload')] },
        { method:'GET', path:'/analysis/payload/{request_id}', fullPath:'GET /api/analysis/payload/{request_id}', desc:'获取分析载荷', params: [P('request_id','string',true,'请求ID')], response: [R('payload','object','machine_payload')] },
        { method:'POST', path:'/analysis/conclusion/save', fullPath:'POST /api/analysis/conclusion/save', desc:'保存分析结论', params: [B('request_id','string',true,'请求ID'),B('conclusion','string',true,'结论内容')], response: [R('conclusion_id','string','')] },
        { method:'GET', path:'/analysis/request/{request_id}/status', fullPath:'GET /api/analysis/request/{request_id}/status', desc:'请求状态', params: [P('request_id','string',true,'请求ID')], response: [R('status','string',''),R('conclusion','string','')] },
        { method:'GET', path:'/analysis/history', fullPath:'GET /api/analysis/history', desc:'分析历史', params: [Q('limit','int',false,'数量')], response: [R('items','array','历史列表')] },
        { method:'GET', path:'/analysis/conclusions', fullPath:'GET /api/analysis/conclusions', desc:'最近结论', params: [Q('limit','int',false,'数量')], response: [R('items','array','结论列表')] },
        { method:'GET', path:'/analysis/chain/{request_id}', fullPath:'GET /api/analysis/chain/{request_id}', desc:'分析全链路追踪', params: [P('request_id','string',true,'请求ID')], response: [R('chain','object','完整调用链')] },
      ]
    },
    {
      key: 'tasks', emoji: '', title: '定时任务管理',
      apis: [
        { method:'GET', path:'/scheduled-tasks/', fullPath:'GET /api/scheduled-tasks/', desc:'任务列表', params: [Q('task_type','string',false,''),Q('status','string',false,'')], response: [R('tasks','array','任务列表')] },
        { method:'GET', path:'/scheduled-tasks/{task_id}', fullPath:'GET /api/scheduled-tasks/{task_id}', desc:'任务详情', params: [P('task_id','string',true,'任务ID')], response: [R('task','object','完整任务信息')] },
        { method:'POST', path:'/scheduled-tasks/', fullPath:'POST /api/scheduled-tasks/', desc:'创建任务', params: [B('task_name','string',true,'任务名'),B('task_type','string',true,'任务类型')], response: [R('task_id','string','')] },
        { method:'PUT', path:'/scheduled-tasks/{task_id}', fullPath:'PUT /api/scheduled-tasks/{task_id}', desc:'更新任务', params: [P('task_id','string',true,'任务ID')], response: [R('success','bool','')] },
        { method:'DELETE', path:'/scheduled-tasks/{task_id}', fullPath:'DELETE /api/scheduled-tasks/{task_id}', desc:'删除任务', params: [P('task_id','string',true,'任务ID')], response: [R('success','bool','')] },
        { method:'POST', path:'/scheduled-tasks/{task_id}/pause', fullPath:'POST /api/scheduled-tasks/{task_id}/pause', desc:'暂停任务', params: [P('task_id','string',true,'任务ID')], response: [R('success','bool','')] },
        { method:'POST', path:'/scheduled-tasks/{task_id}/resume', fullPath:'POST /api/scheduled-tasks/{task_id}/resume', desc:'恢复任务', params: [P('task_id','string',true,'任务ID')], response: [R('success','bool','')] },
        { method:'POST', path:'/scheduled-tasks/{task_id}/run', fullPath:'POST /api/scheduled-tasks/{task_id}/run', desc:'手动触发', params: [P('task_id','string',true,'任务ID')], response: [R('success','bool','')] },
        { method:'GET', path:'/scheduled-tasks/runs/', fullPath:'GET /api/scheduled-tasks/runs/', desc:'运行记录列表', params: [Q('task_id','string',false,''),Q('limit','int',false,'50')], response: [R('runs','array','运行记录')] },
        { method:'GET', path:'/scheduled-tasks/runs/{run_id}', fullPath:'GET /api/scheduled-tasks/runs/{run_id}', desc:'运行记录详情', params: [P('run_id','string',true,'运行记录ID')], response: [R('run','object','含machine_payload')] },
        { method:'GET', path:'/scheduled-tasks/health-check', fullPath:'GET /api/scheduled-tasks/health-check', desc:'系统自检', params: [], response: [R('result','object','自检结果')] },
      ]
    },
    {
      key: 'conclusion', emoji: '', title: '分析结论管理',
      apis: [
        { method:'GET', path:'/analysis-conclusion/list', fullPath:'GET /api/analysis-conclusion/list', desc:'结论列表', params: [Q('ts_code','string',false,'股票代码'),Q('limit','int',false,'数量')], response: [R('conclusions','array','结论列表')] },
        { method:'POST', path:'/analysis-conclusion/save', fullPath:'POST /api/analysis-conclusion/save', desc:'保存结论留痕', params: [B('ts_code','string',true,'股票代码'),B('summary','string',true,'摘要')], response: [R('conclusion_id','string','')] },
        { method:'GET', path:'/analysis-conclusion/{ts_code}', fullPath:'GET /api/analysis-conclusion/{ts_code}', desc:'股票最近N次结论', params: [P('ts_code','string',true,'股票代码'),Q('limit','int',false,'数量')], response: [R('conclusions','array','结论列表')] },
        { method:'DELETE', path:'/analysis-conclusion/{conclusion_id}', fullPath:'DELETE /api/analysis-conclusion/{conclusion_id}', desc:'删除结论', params: [P('conclusion_id','string',true,'结论ID')], response: [R('success','bool','')] },
      ]
    },
    {
      key: 'system', emoji: '', title: '系统设置',
      apis: [
        { method:'GET', path:'/system-settings/config', fullPath:'GET /api/system-settings/config', desc:'系统配置', params: [], response: [R('config','object','完整系统配置')] },
        { method:'GET', path:'/system-settings/info', fullPath:'GET /api/system-settings/info', desc:'系统信息', params: [], response: [R('version','string',''),R('uptime','string','')] },
        { method:'GET', path:'/system-settings/health-check', fullPath:'GET /api/system-settings/health-check', desc:'健康检查(管理)', params: [], response: [R('status','string',''),R('checks','array','各项检查结果')] },
      ]
    },
    {
      key: 'logs', emoji: '', title: '日志与数据源',
      apis: [
        { method:'GET', path:'/log/list', fullPath:'GET /api/log/list', desc:'日志列表', params: [Q('limit','int',false,'数量')], response: [R('logs','array','日志记录')] },
        { method:'GET', path:'/log/{log_id}', fullPath:'GET /api/log/{log_id}', desc:'日志详情', params: [P('log_id','string',true,'日志ID')], response: [R('log','object','完整日志记录')] },
        { method:'GET', path:'/data-source/check', fullPath:'GET /api/data-source/check', desc:'数据源连通性检查', params: [], response: [R('tushare','object','{status,message}')] },
        { method:'GET', path:'/data-source/tushare/status', fullPath:'GET /api/data-source/tushare/status', desc:'Tushare状态', params: [], response: [R('token_configured','bool',''),R('quota','object','')] },
      ]
    },
    {
      key: 'notify', emoji: '', title: '通知管理',
      apis: [
        { method:'POST', path:'/notification/test', fullPath:'POST /api/notification/test', desc:'测试通知发送', params: [B('channel','string',true,'通知渠道'),B('content','string',true,'内容')], response: [R('success','bool','')] },
        { method:'GET', path:'/notification/logs', fullPath:'GET /api/notification/logs', desc:'通知发送日志', params: [Q('limit','int',false,'数量')], response: [R('logs','array','通知记录')] },
      ]
    },
  ]
}

export default {
  name: 'ApiDocs',
  data() {
    return {
      groups: buildGroups(),
      search: '',
      activeGroups: ['firstbook', 'stock_kline', 'chat'],
    }
  },
  computed: {
    totalCount() {
      return this.groups.reduce((sum, g) => sum + g.apis.length, 0)
    },
    filteredGroups() {
      if (!this.search) return this.groups
      const kw = this.search.toLowerCase()
      return this.groups.map(g => ({
        ...g,
        apis: g.apis.filter(a => this.filterMatch(a))
      })).filter(g => g.apis.length > 0)
    },
  },
  methods: {
    methodType(m) { return METHODS[m] || '' },
    filterMatch(api) {
      if (!this.search) return true
      const kw = this.search.toLowerCase()
      return (api.fullPath || '').toLowerCase().includes(kw) ||
             (api.desc || '').toLowerCase().includes(kw) ||
             (api.path || '').toLowerCase().includes(kw)
    },
    scrollToGroup(key) {
      const group = this.groups.find(g => g.key === key)
      if (group && !this.activeGroups.includes(key)) {
        this.activeGroups.push(key)
      }
      this.$nextTick(() => {
        const el = document.querySelector(`[name="${key}"]`)
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' })
      })
    },
  },
}
</script>

<style scoped>
.apidocs-page { padding-bottom: 40px; }

.entry-card {
  cursor: pointer;
  transition: all 0.3s;
}
.entry-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0,0,0,0.1);
}
.entry-card .el-card__body {
  display: flex;
  align-items: center;
  padding: 16px;
}
.entry-icon {
  width: 44px;
  height: 44px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 22px;
  flex-shrink: 0;
}
.entry-content {
  margin-left: 12px;
  flex: 1;
}
.entry-title {
  font-weight: 600;
  font-size: 14px;
  color: #303133;
}
.entry-desc {
  font-size: 12px;
  color: #909399;
  margin-top: 2px;
}

.group-title-row {
  display: flex;
  align-items: center;
  width: 100%;
}
.group-emoji { font-size: 18px; margin-right: 8px; }
.group-name { font-weight: 600; font-size: 14px; }

.api-path {
  font-family: 'Consolas', 'Monaco', 'Courier New', monospace;
  font-size: 12px;
  color: #409eff;
  background: #f0f5ff;
  padding: 2px 6px;
  border-radius: 3px;
  white-space: nowrap;
}

.endpoint-detail {
  padding: 12px 24px;
  background: #fafbfc;
}
.detail-section {
  margin-bottom: 16px;
}
.detail-section:last-child { margin-bottom: 0; }
.detail-section h4 {
  font-size: 13px; color: #303133; margin: 0 0 8px 0;
}
.detail-empty {
  text-align: center; color: #c0c4cc; font-size: 13px; padding: 16px 0;
}
</style>
